import React, { forwardRef, useState, useCallback, useRef, useEffect } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  FieldShellLeadSlot,
  fieldShellInnerInputClass,
  fieldShellWrapperClass,
} from '@/components/ui/field-shell';

/* ─── Types ─────────────────────────────────────────────────── */
export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  value?: string;
  defaultValue?: string;
  onSearch: (value: string) => void;
  debounceMs?: number;
  loading?: boolean;
  showShortcut?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
  wrapperClassName?: string;
}

const sizeStyles = {
  sm: 'h-8 text-xs',
  md: 'h-10 text-sm',
  lg: 'h-12 text-base',
} as const;

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      wrapperClassName,
      value: controlledValue,
      defaultValue = '',
      onSearch,
      debounceMs = 300,
      loading = false,
      showShortcut = true,
      inputSize = 'md',
      placeholder = 'Search…',
      ...props
    },
    ref
  ) => {
    const [internal, setInternal] = useState(defaultValue);
    const value = controlledValue ?? internal;
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRef = useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      },
      [ref]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        if (controlledValue === undefined) setInternal(v);

        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => onSearch(v), debounceMs);
      },
      [controlledValue, debounceMs, onSearch]
    );

    const clear = useCallback(() => {
      if (controlledValue === undefined) setInternal('');
      clearTimeout(debounceRef.current);
      onSearch('');
      inputRef.current?.focus();
    }, [controlledValue, onSearch]);

    // Keyboard shortcut
    useEffect(() => {
      if (!showShortcut) return;
      const handler = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          inputRef.current?.focus();
        }
      };
      document.addEventListener('keydown', handler);
      return () => document.removeEventListener('keydown', handler);
    }, [showShortcut]);

    // Cleanup debounce
    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const dense = inputSize === 'sm';
    const lead = loading ? (
      <Loader2 className="h-4 w-4 animate-spin opacity-90" aria-hidden="true" />
    ) : (
      <Search className="h-4 w-4 opacity-90" aria-hidden="true" />
    );

    return (
      <div className={cn(fieldShellWrapperClass, 'relative pe-1', wrapperClassName)}>
        <FieldShellLeadSlot dense={dense}>{lead}</FieldShellLeadSlot>
        <input
          ref={setRef}
          type="search"
          role="searchbox"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          aria-label={(props as { 'aria-label'?: string })['aria-label'] ?? placeholder ?? 'Recherche'}
          className={cn(fieldShellInnerInputClass(dense), sizeStyles[inputSize], className)}
          {...props}
        />

        <span className="flex shrink-0 items-center gap-1 pe-1.5">
          {value && (
            <button
              type="button"
              onClick={clear}
              className={cn(
                'rounded-md p-0.5 text-gray-500 hover:text-gray-800',
                'dark:text-gray-400 dark:hover:text-gray-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
              )}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {showShortcut && !value && (
            <kbd className="hidden h-5 shrink-0 items-center rounded border border-gray-200 bg-gray-50 px-1.5 text-[10px] font-medium text-gray-400 sm:inline-flex dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500">
              ⌘K
            </kbd>
          )}
        </span>
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
